export type ApplicationStateKind =
  | 'loading'
  | 'empty'
  | 'partial'
  | 'network'
  | 'unavailable'
  | 'provider_failure'
  | 'queue_delay'
  | 'credits_exhausted'
  | 'subscription_required'
  | 'addon_required'
  | 'permission_denied'
  | 'unknown_error';

export interface ApplicationStateResolution {
  kind: ApplicationStateKind;
  retryable: boolean;
  /** A stable, non-sensitive support/analytics identifier. */
  safeCode: string;
}

export interface ApplicationFailureCopy extends ApplicationStateResolution {
  /** Safe, localized copy suitable for rendering directly in the product UI. */
  title: string;
  description: string;
}

export type ApplicationStateTranslator = (key: string) => string;

type FailureShape = {
  code?: unknown;
  status?: unknown;
  message?: unknown;
  name?: unknown;
};

function normalized(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function statusOf(value: FailureShape): number | null {
  return typeof value.status === 'number' && Number.isInteger(value.status)
    ? value.status
    : null;
}

function has(value: string, token: string): boolean {
  return value === token || value.includes(token);
}

/**
 * Converts untrusted API/client failures into a small set of safe UI states.
 * It never returns the original message, stack, request payload, or provider
 * response, so callers can render it directly without leaking internals.
 */
export function resolveApplicationFailure(error: unknown): ApplicationStateResolution {
  const value = (error && typeof error === 'object' ? error : {}) as FailureShape;
  const code = normalized(value.code) || normalized(value.message);
  const status = statusOf(value);
  const name = normalized(value.name);

  if (
    status === 401 ||
    status === 403 ||
    has(code, 'permission') ||
    has(code, 'forbidden') ||
    has(code, 'authentication_required') ||
    has(code, 'admin_required') ||
    has(code, 'access_denied') ||
    has(code, 'application_boundary')
  ) {
    return { kind: 'permission_denied', retryable: false, safeCode: 'permission_denied' };
  }
  if (has(code, 'insufficient_credit') || has(code, 'credit_exhaust') || has(code, 'credit_required')) {
    return { kind: 'credits_exhausted', retryable: false, safeCode: 'credits_exhausted' };
  }
  if (
    has(code, 'subscription_required') ||
    has(code, 'plan_required') ||
    has(code, 'entitlement_required') ||
    has(code, 'not_entitled') ||
    has(code, 'entitlement_limit')
  ) {
    return { kind: 'subscription_required', retryable: false, safeCode: 'subscription_required' };
  }
  if (has(code, 'addon_required') || has(code, 'add_on_required')) {
    return { kind: 'addon_required', retryable: false, safeCode: 'addon_required' };
  }
  if (has(code, 'provider') || has(code, 'model_unavailable')) {
    return { kind: 'provider_failure', retryable: true, safeCode: 'provider_failure' };
  }
  if (status === 429 || has(code, 'queue') || has(code, 'rate_limit') || has(code, 'rate-limited')) {
    return { kind: 'queue_delay', retryable: true, safeCode: 'queue_delay' };
  }
  if (status !== null && status >= 500) {
    return { kind: 'unavailable', retryable: true, safeCode: 'service_unavailable' };
  }
  if (name === 'typeerror' || has(code, 'network') || has(code, 'failed to fetch')) {
    return { kind: 'network', retryable: true, safeCode: 'network_error' };
  }
  return { kind: 'unknown_error', retryable: true, safeCode: 'unexpected_error' };
}

/**
 * Pairs a resolved failure with product-owned localized copy. The translator
 * only receives our static `state.*` keys; untrusted error content is never
 * passed through to it or returned from this function.
 */
export function applicationFailureCopy(
  error: unknown,
  translate: ApplicationStateTranslator,
): ApplicationFailureCopy {
  const resolution = resolveApplicationFailure(error);
  return {
    ...resolution,
    title: translate(`state.${resolution.kind}_title`),
    description: translate(`state.${resolution.kind}_body`),
  };
}
